import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatInputModule} from '@angular/material/input';
import {ChatService} from '../../services/chat.service';
import {SideBarService} from '../../services/sidebar.service';
import {PinnedQueryService} from '../../services/pinned-query.service';
import {MessageType} from '../../enum/constants.enum'
import {v4 as uuidv4} from 'uuid';
import {NgForOf, NgIf} from '@angular/common';
import { map, Subscription } from 'rxjs';
import { PostMessageService, WindowPostMessageAction } from '../../services/post-message.service';
import { TextFieldModule } from '@angular/cdk/text-field';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'fyxt-send-message',
    templateUrl: './message.component.html',
    styleUrls: ['./message.component.scss'],
    standalone: true,
    imports: [MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, FormsModule, MatProgressSpinnerModule, NgIf, NgForOf, TextFieldModule]
})
export class SendMessageComponent implements OnInit, OnDestroy {
  @Input() questionMessage!: string;
  public isNewSession: boolean = false;
  public type = MessageType;
  public streamRequest!: Subscription;
  public useLanggraph = environment.useLanggraph;
  private sendQuerySub?: Subscription;

  public get questions(): {text: string}[] {
    return this.chatService.templateQuestions || [];
  }

  constructor(
    public chatService: ChatService,
    private sideBarService: SideBarService,
    private postMessageService: PostMessageService,
    private pinnedQueryService: PinnedQueryService,
  ) {
  }

  ngOnInit(): void {
    this.sendQuerySub = this.pinnedQueryService.sendQueryRequest$.subscribe((text) => {
      this.pinnedQueryService.clearPendingSend();
      this.questionMessage = text;
      this.sendMessage();
      setTimeout(() => {
        this.chatService.scrollToBottom$.next();
      }, 300);
    });
  }

  ngOnDestroy(): void {
    this.sendQuerySub?.unsubscribe();
  }

  sendMessage(e?: any) {
    if (!this.chatService.isLoading && this.questionMessage.trim()) {
      if (e) e.preventDefault();
      this.chatService.isLoading = true;
      let text = this.questionMessage;

      this.questionMessage = '';
      if (!this.chatService.messagesSubject.value.length) {
        this.chatService.currentSessionId = this.chatService.currentSessionId || uuidv4();
        this.sideBarService.currentChat = this.chatService.currentSessionId;
        const data = { message: this.chatService.currentSessionId };
        this.postMessageService.sendMessage(this.chatService.currentSessionId, WindowPostMessageAction.SET_SESSION_ID);
        this.sideBarService.storageSession = '';
        this.isNewSession = true;
      } else {
        this.chatService.currentSessionId = this.chatService.messagesSubject.value[this.chatService.messagesSubject.value.length - 1].session_id;
      }

      const currentMessages = this.chatService.messagesSubject.getValue();
      this.chatService.messagesSubject.next([ { id: '', sender: this.type.USER, text: text, session_id: this.chatService.currentSessionId}, ...currentMessages])

      if (this.chatService.isStream) {
        this.streamRequest = this.chatService.runStream(text)
          .subscribe({
            next: (res) => {
              const stream_url = res.outputs[0].outputs[0].artifacts.stream_url;
              this.chatService.currentSessionId = res.session_id;
              const current = this.chatService.messagesSubject.getValue();
              this.chatService.messagesSubject.next([
                {
                  id: '',
                  sender: this.type.MACHINE,
                  text: '',
                  reaction: '',
                  session_id: res.session_id,
                  isError: false,
                  isPending: true,
                },
                ...current
              ])
              this.chatService.getStream(stream_url, res.session_id)
              this.updateSessionList();
            },
            error: (res) => {
              this.chatService.isLoading = false;
              this.chatService.messagesSubject.value[this.chatService.messagesSubject.value.length - 1] = {
                id: '',
                sender: this.type.MACHINE,
                text: "Sorry, something went wrong.",
                isError: true,
                session_id: res.session_id
              }
              this.updateSessionList();
            }
          });
      } else if (this.useLanggraph) {
        // LangGraph streaming
        console.log('Starting streaming request...');
        const current = this.chatService.messagesSubject.getValue();

        // Remove any existing pending messages before adding new one
        const filteredMessages = current.filter(msg => !msg.isPending);

        console.log('Filtered messages (removed pending):', filteredMessages.length);

        // Add empty AI message with pending state - this should show just dots
        const newPendingMessage = {
          sender: this.type.MACHINE,
          text: '',
          reaction: '',
          id: '',
          isPending: true,
          session_id: this.chatService.currentSessionId || uuidv4(),
        };

        console.log('Adding new pending message with empty text');
        this.chatService.messagesSubject.next([
          newPendingMessage,
          ...filteredMessages
        ])

        this.chatService.sendMessageWithStreaming(
          text,
          (chunk) => {
            // Don't process chunks if stream was cancelled
            // This check is also done in ChatService, but adding here for safety
            if (this.chatService.isStreamCancelled) {
              return;
            }

            // Handle error chunks - show error immediately
            if (chunk.type === 'error') {
              console.error('Streaming error chunk:', chunk);
              const reverseMessages = [...this.chatService.messagesSubject.value].reverse();
              const lastMessage = reverseMessages[reverseMessages.length - 1];

              // Show generic error message
              lastMessage.text = 'Sorry, something went wrong.';
              lastMessage.isError = true;
              lastMessage.isPending = false;
              reverseMessages[reverseMessages.length - 1] = { ...lastMessage };
              reverseMessages.reverse();
              this.chatService.messagesSubject.next(reverseMessages);
              this.chatService.isLoading = false;
              return;
            }

            // Handle token chunks (token-by-token streaming)
            if (chunk.type === 'token' && chunk.content) {
              const reverseMessages = [...this.chatService.messagesSubject.value].reverse();
              const lastMessage = reverseMessages[reverseMessages.length - 1];

              // Append new tokens to existing text
              lastMessage.text = (lastMessage.text || '') + chunk.content;
              lastMessage.isPending = true;
              reverseMessages[reverseMessages.length - 1] = { ...lastMessage };
              reverseMessages.reverse();
              this.chatService.messagesSubject.next(reverseMessages);
              return;
            }

            // Skip metadata-only chunks (run_id, attempt, etc.)
            if (chunk.run_id && !chunk.messages && !chunk.values && !chunk.content && !chunk.output && !chunk.agent) {
              return;
            }

            const reverseMessages = [...this.chatService.messagesSubject.value].reverse();
            const lastMessage = reverseMessages[reverseMessages.length - 1];

            // LangGraph sends chunks with different structures
            // Try to extract AI message from various possible formats
            let aiContent = '';

            // Handle agent node updates (stream_mode: 'updates')
            if (chunk.agent && chunk.agent.messages) {
              const aiMessages = chunk.agent.messages.filter((m: any) => m.type === 'ai' || m.type === 'AIMessage');
              if (aiMessages.length > 0) {
                const lastAiMessage = aiMessages[aiMessages.length - 1];
                if (lastAiMessage.content) {
                  aiContent = lastAiMessage.content;

                  // Try to parse JSON response during streaming
                  try {
                    const parsed = JSON.parse(aiContent);
                    // If it's the structured response, extract the answer
                    if (parsed.answer) {
                      aiContent = parsed.answer;
                    }
                  } catch {
                    // Not JSON or incomplete JSON, use as-is
                  }
                }
              }
            } else if (chunk.values && chunk.values.messages) {
              const aiMessages = chunk.values.messages.filter((m: any) => m.type === 'ai' || m.type === 'AIMessage');
              if (aiMessages.length > 0) {
                const lastAiMessage = aiMessages[aiMessages.length - 1];
                if (lastAiMessage.content) {
                  aiContent = lastAiMessage.content;

                  // Try to parse JSON response during streaming
                  try {
                    const parsed = JSON.parse(aiContent);
                    // If it's the structured response, extract the answer
                    if (parsed.answer) {
                      aiContent = parsed.answer;
                    }
                  } catch {
                    // Not JSON or incomplete JSON, use as-is
                  }
                }
              }
            } else if (chunk.content) {
              aiContent = chunk.content;
            }

            // Update if we have content (allow empty string to clear, but skip if same as current)
            if (!!aiContent && aiContent !== lastMessage.text) {
              lastMessage.text = aiContent;
              lastMessage.isPending = true;
              reverseMessages[reverseMessages.length - 1] = { ...lastMessage };
              reverseMessages.reverse();
              this.chatService.messagesSubject.next(reverseMessages);
            }
          },
          (result) => {
            // Don't process completion if stream was cancelled
            if (this.chatService.isStreamCancelled) {
              console.log('Completion handler ignored - stream was cancelled');
              // Ensure isLoading is false even if stream was cancelled
              this.chatService.isLoading = false;
              return;
            }

            // Handle completion
            // Messages are stored with newest first (index 0 is most recent)
            const allMessages = [...this.chatService.messagesSubject.value];

            // Try to find message by messageId first, then fall back to first message (newest)
            let lastMessage: any = null;
            let messageIndex = -1;

            if (result.messageId) {
              // Find message by ID (check both id and messageId fields)
              messageIndex = allMessages.findIndex((m: any) => m.id === result.messageId || m.messageId === result.messageId);
              if (messageIndex >= 0) {
                lastMessage = allMessages[messageIndex];
                console.log('Found message by messageId at index:', messageIndex, result.messageId);
              } else {
                console.warn('Message not found by messageId, using first message (newest)');
              }
            }

            // Fallback to first message (newest) if not found by ID
            if (!lastMessage && allMessages.length > 0) {
              messageIndex = 0;
              lastMessage = allMessages[0];
            }
            // If the last message is still pending, that's okay - we'll update it
            // Don't return early, just proceed with the update
            if (!lastMessage) {
              console.error('Completion handler: last message is missing!');
              // Ensure isLoading is false even if message is missing
              this.chatService.isLoading = false;
              return;
            }

            // Extract metadata from completion event
            let csvLink = '';
            let queryId = '';
            let suggestedQuestion = '';
            let csvLinks: Array<{ tool_name: string; csv_link: string; query_id: string }> = [];

            // Get metadata from completion event (result is now the full data object)
            if (result.metadata) {
              csvLink = result.metadata.csv_link || '';
              queryId = result.metadata.query_id || '';
              suggestedQuestion = result.metadata.suggested_question || '';
              if (result.metadata.reasoning) {
                lastMessage.reasoning = result.metadata.reasoning;
              }
              if (result.metadata.csv_links && Array.isArray(result.metadata.csv_links)) {
                csvLinks = result.metadata.csv_links;
              }
            }

            // Fallback: Extract from result.result.output if metadata not available
            // result.result contains the langGraphRes from the API
            const langGraphRes = result.result || result;

            // Handle both array and object output formats
            let messages: any[] = [];
            if (langGraphRes.output) {
              // Check if output is an array (new format) or object with messages property (old format)
              if (Array.isArray(langGraphRes.output)) {
                messages = langGraphRes.output;
              } else if (langGraphRes.output.messages && Array.isArray(langGraphRes.output.messages)) {
                messages = langGraphRes.output.messages;
              }
            }

            if (messages.length > 0) {
              // Check all tool messages (not just sqllm_query, as MCP tools also return signed URLs)
              const toolMessages = messages.filter((m: any) => m.type === 'tool');
              if (toolMessages.length > 0) {
                // Check all tool messages, starting from the last one
                for (let i = toolMessages.length - 1; i >= 0; i--) {
                  const toolMessage = toolMessages[i];
                  try {
                    let toolResult;
                    if (typeof toolMessage.content === 'string') {
                      toolResult = JSON.parse(toolMessage.content);
                    } else if (toolMessage.content && typeof toolMessage.content === 'object') {
                      toolResult = toolMessage.content;
                    } else {
                      toolResult = toolMessage.content;
                    }

                    // Extract signed URL from any tool result (MCP tools also return signed_url)
                    const extractedSignedUrl = toolResult.signed_url || toolResult.signedUrl || toolResult.csv_download_url || toolResult.csv_link || '';
                    const extractedQueryId = toolResult.queryId || toolResult.query_id || '';

                    if (extractedSignedUrl && !csvLink) {
                      csvLink = extractedSignedUrl;
                    }
                    if (extractedQueryId && !queryId) {
                      queryId = extractedQueryId;
                    }

                    // If we found both, we can stop searching
                    if (csvLink && queryId) {
                      break;
                    }
                  } catch (e) {
                    console.error('Failed to parse tool message:', e);
                    // Continue to next tool message
                  }
                }
              }

              // Get all AI messages
              const aiMessages = messages.filter((m: any) => m.type === 'ai');

              // Get the last AI message (final response after tool calls)
              if (aiMessages.length > 0) {
                const lastAiMessage = aiMessages[aiMessages.length - 1];
                if (lastAiMessage.content) {
                  // Try to parse as JSON to extract structured data
                  try {
                    const parsed = JSON.parse(lastAiMessage.content);
                    // Only override if not already set from metadata
                    csvLink = csvLink || parsed.csv_link || '';
                    queryId = queryId || parsed.query_id || '';
                    suggestedQuestion = suggestedQuestion || parsed.suggested_question || '';
                  } catch {
                    // If not JSON, use existing values
                  }
                }
              }
            }

            // If we have a messageId but no CSV link, try to fetch it from the backend
            // This handles cases where CSV is generated but link is not in the completion event
            if (!csvLink && result.messageId) {
              console.log('No CSV link in completion event, messageId:', result.messageId);
              // The CSV link will be available when the message is loaded from history
              // For now, we'll set it to null and it will be populated when message is saved/loaded
            }

            // Set text_output for suggested question and metadata
            // Always set text_output if we have any metadata, even if csv_link is empty
            // The backend will populate csv_link when the message is saved
            const messageId = result.messageId || '';
            if (queryId || suggestedQuestion || csvLink || messageId) {
              lastMessage.text_output = {
                id: messageId,
                sender: this.type.MACHINE,
                text: JSON.stringify({
                  query_id: queryId,
                  answer: lastMessage.text || 'No response generated',
                  suggested_question: suggestedQuestion,
                  csv_link: csvLink || '', // May be empty initially, will be populated by backend
                  csv_links: csvLinks.length > 0 ? csvLinks : []
                })
              };
            }

            // Set multiple download URLs if available
            if (csvLinks.length > 0) {
              lastMessage.download_urls = csvLinks;
            }

            // Set CSV download link if available
            // Also check text_output in case csv_link is there but download_url wasn't set
            if (csvLink) {
              lastMessage.download_url = csvLink;
            } else if (lastMessage.text_output && lastMessage.text_output.text) {
              // Try to extract from text_output (for consistency with history loading)
              try {
                const textOutputParsed = JSON.parse(lastMessage.text_output.text);
                if (textOutputParsed.csv_link) {
                  lastMessage.download_url = textOutputParsed.csv_link;
                  csvLink = textOutputParsed.csv_link;
                }
              } catch (e) {
                console.error('Failed to parse text_output:', e);
              }
            }

            if (!csvLink) {
              console.warn('⚠️ No CSV link found in metadata or tool results. MessageId:', result.messageId);
            } else {
              console.log('✅ CSV link found and will be set on message');
            }

            lastMessage.isPending = false;
            lastMessage.isError = false; // Ensure error flag is cleared on successful completion
            // Get thread_id from result.result (langGraphRes) or result itself
            const threadId = langGraphRes.thread_id || result.thread_id || this.chatService.currentSessionId || uuidv4();
            lastMessage.session_id = threadId;

            // Set message ID from backend response
            if (result.messageId) {
              lastMessage.id = result.messageId;
              console.log('Set message ID:', result.messageId);
            }

            // Update current session ID to use the same thread for next messages
            if (threadId) {
              this.chatService.currentSessionId = threadId || uuidv4();
            }

            // Update UI with final message - ensure download_url is preserved
            const finalMessage = { ...lastMessage };

            // Update the message at the found index (preserve original order)
            if (messageIndex >= 0) {
              allMessages[messageIndex] = finalMessage;
            } else {
              // Fallback: update first message (newest)
              if (allMessages.length > 0) {
                allMessages[0] = finalMessage;
                console.warn('Message not found by ID, updated first message (newest) as fallback');
              } else {
                console.error('No messages in array to update!');
              }
            }

            // Update messages without reversing - preserve original order (newest first)
            this.chatService.messagesSubject.next(allMessages);

            // Set isLoading to false AFTER updating messages so scroll logic can still run
            // The messages$ subscription will see the update and handle scrolling
            this.chatService.isLoading = false;

            // Trigger scroll to bottom after message update (especially important when download_url is added)
            // Use requestAnimationFrame to ensure DOM has updated and scroll happens after isLoading is set
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.chatService.scrollToBottom$.next();
              });
            });
            this.updateSessionList();

            // Only refetch when we expect the backend to add csv_link (e.g. after a query).
            // For messages without queryId/csvLink, do not refetch: loadChatMessages replaces the whole list,
            // and if the backend hasn't persisted the message yet, the just-finished message would disappear.
            if (messageId && !csvLink && queryId) {
              setTimeout(() => this.fetchMessageDetails(), 1000);
            }
          },
          (error) => {
            // Handle error
            console.error('Streaming error:', error);
            this.chatService.isLoading = false;
            const reverseMessages = [...this.chatService.messagesSubject.value].reverse();
            reverseMessages[reverseMessages.length - 1] = {
              id: '',
              sender: this.type.MACHINE,
              text: "Sorry, something went wrong.",
              isError: true,
              session_id: this.chatService.currentSessionId || uuidv4()
            }
            reverseMessages.reverse()
            this.chatService.messagesSubject.next(reverseMessages);
            this.updateSessionList();
          }
        );
      } else {
        // Non-streaming (original)
        const current = this.chatService.messagesSubject.getValue();

        this.chatService.messagesSubject.next([
          {
            sender: this.type.MACHINE,
            text: '',
            reaction: '',
            id: '',
          },
          ...current
        ])

        this.streamRequest = this.chatService.sendMessage(text).pipe(map((res) => res.data))
          .subscribe({
            next: (res) => {
              const messageOutput = res?.text
              const downloadUrl = res?.csv_link || null
              const textOutput = res.text_output

              const reverseMessages = [...this.chatService.messagesSubject.value].reverse();
              this.chatService.currentSessionId = res?.session_id || uuidv4();
              reverseMessages[reverseMessages.length - 1] = {
                id: res?.id || '',
                sender: this.type.MACHINE,
                text: messageOutput,
                download_url: downloadUrl,
                isError: false,
                session_id: res?.session_id,
                text_output: textOutput,
                ...(res?.reasoning != null && { reasoning: res.reasoning }),
              }
              reverseMessages.reverse()
              this.chatService.messagesSubject.next(reverseMessages);
              this.chatService.isLoading = false;
              this.updateSessionList();
            },
            error: (err) => {
              this.chatService.isLoading = false;
              const reverseMessages = [...this.chatService.messagesSubject.value].reverse();
              reverseMessages[reverseMessages.length - 1] = {
                id: '',
                sender: this.type.MACHINE,
                text: "Sorry, something went wrong.",
                isError: true,
                session_id: err.session_id
              }
              reverseMessages.reverse()
              this.chatService.messagesSubject.next(reverseMessages);
              this.updateSessionList();
            }
          });
      }
    }
  }

  updateSessionList() {
    if (this.isNewSession) {
      this.sideBarService.loadShortSessionsInfo();
    }
    this.isNewSession = false;
  }

  /**
   * Refetch session messages in background to get updated csv_link from backend.
   * Uses showLoader: false so the loader is not shown.
   */
  private fetchMessageDetails() {
    if (!this.chatService.currentSessionId) {
      return;
    }
    this.chatService.loadChatMessages(this.chatService.currentSessionId, { showLoader: false });
  }

  async stopSending() {
    let messageIdToDelete: string | null = null;

    // Get the pending message and user message before cancelling
    // Messages are stored with newest first, so the first message is the most recent
    const currentMessages = [...this.chatService.messagesSubject.value];
    const pendingMessageIndex = currentMessages.findIndex(msg => msg.isPending);
    const pendingMessage = pendingMessageIndex >= 0 ? currentMessages[pendingMessageIndex] : null;
    const pendingMessageId = pendingMessage?.id || null;

    // Find the user message that triggered this stream (should be right after the pending message)
    // In LangGraph flow: [pending AI message (index 0), user message (index 1), ...]
    let userMessageIndex = -1;
    let userMessageId: string | null = null;

    if (pendingMessageIndex >= 0 && pendingMessageIndex + 1 < currentMessages.length) {
      const nextMessage = currentMessages[pendingMessageIndex + 1];
      if (nextMessage.sender === this.type.USER) {
        userMessageIndex = pendingMessageIndex + 1;
        userMessageId = nextMessage.id || null;
      }
    }

    // Fallback: if no pending message found, check if first message is user message
    if (pendingMessageIndex < 0 && currentMessages.length > 0) {
      const firstMessage = currentMessages[0];
      if (firstMessage.sender === this.type.USER) {
        userMessageIndex = 0;
        userMessageId = firstMessage.id || null;
      }
    }

    console.log('stopSending: Found messages', {
      totalMessages: currentMessages.length,
      pendingMessageIndex,
      pendingMessageId,
      userMessageIndex,
      userMessageId,
    });

    // Cancel the stream if using LangGraph
    if (this.useLanggraph) {
      messageIdToDelete = await this.chatService.cancelStream();
    } else if (this.streamRequest) {
      // For non-LangGraph streams, just unsubscribe
      this.streamRequest.unsubscribe();
    }

    // Update UI state
    this.chatService.isLoading = false;

    // Remove messages from UI (both pending AI message and user message)
    const messagesToRemove: number[] = [];

    if (pendingMessageIndex >= 0) {
      messagesToRemove.push(pendingMessageIndex);
    }

    if (userMessageIndex >= 0) {
      messagesToRemove.push(userMessageIndex);
    }

    // Remove messages in reverse order to maintain correct indices
    messagesToRemove.sort((a, b) => b - a).forEach(index => {
      currentMessages.splice(index, 1);
    });

    if (messagesToRemove.length > 0) {
      console.log('stopSending: Removed', messagesToRemove.length, 'message(s), remaining:', currentMessages.length);
      this.chatService.messagesSubject.next(currentMessages);

      // Delete from database if message IDs exist
      const idsToDelete = [
        messageIdToDelete,
        pendingMessageId,
        userMessageId
      ].filter((id): id is string => id !== null && id !== undefined && id.trim() !== '');

      for (const id of idsToDelete) {
        try {
          await this.chatService.deleteMessage(id);
          console.log('stopSending: Deleted message from database:', id);
        } catch (error) {
          console.error('Failed to delete message from database:', error);
          // Continue even if deletion fails - message is already removed from UI
        }
      }
    } else {
      // Fallback: if no messages found by index, try to remove first empty AI message
      console.log('stopSending: No messages found by index, checking first message');
      if (currentMessages.length > 0) {
        const firstMessage = currentMessages[0];
        // Check if it's an empty AI message (likely the pending one)
        if ((!firstMessage.text || firstMessage.text.trim() === '') &&
            firstMessage.sender === this.type.MACHINE) {
          currentMessages.shift(); // Remove first message

          // Also remove user message if it's next
          if (currentMessages.length > 0 && currentMessages[0].sender === this.type.USER) {
            currentMessages.shift();
          }

          console.log('stopSending: Removed empty messages, remaining:', currentMessages.length);
          this.chatService.messagesSubject.next(currentMessages);
        }
      }
    }
  }

  selectTemplateQuestion(question: {text: string}) {
    this.questionMessage = question.text;
    this.sendMessage();
    // Trigger scroll to bottom after message is added to DOM
    setTimeout(() => {
      this.chatService.scrollToBottom$.next();
    }, 300);
  }

}
